import uiRouter from '@uirouter/angularjs';
import ngTranslate from 'angular-translate';
import reduce from 'lodash/reduce';
import isString from 'lodash/isString';

import { AppSettingsModule } from 'services/settings.provider';
import { DataServiceModule } from './data.service';
import { StoreAddressesModule } from './storeAddresses.service';
import { StoreInformationServiceModule } from './storeInformation.service';

export const PostageServiceModule = angular
    .module('app.services.postage', [
        'ngMaterial',
        uiRouter,
        ngTranslate,

        DataServiceModule,
        AppSettingsModule,
        StoreInformationServiceModule,
        StoreAddressesModule
    ]).constant('LABEL_FILE_TYPES', [
        { label: 'Original file (PNG)', code: 'PNG' },
        { label: '8.5x11in PDF', code: 'PDF' },
        { label: '4x6in PDF', code: 'PDF_4x6' },
        { label: 'ZPL II Image Format', code: 'ZPLII' }
    ]).service('postageService', (
        dataService,
        settings,
        $mdDialog,
        $q,
        storeInformationService,
        storeAddressesService,
        $filter,
        $mdToast,
        $state,
        $translate
    ) => {
        let service = {
            getRates,
            getDefaults,
            createDefaults,
            purchaseLabel,
            getPurchasedLabel,
            refundLabel,
            saveDefaults,
            openRefundShippingLabelModal,
            generateRecipient,
            generateSender,
            getWeightOfCartItems,
            getQuantityOfCartItems,
            getCarrierFromToken,
            getNumOfItemsMissingWeight,
            showErrorToast,
            hasConfiguredDefaults,
            openRedirectToSetupModal
        };

        function getRates(customer, sender, parcel) {
            return dataService.create(settings.api.postage, {
                to: customer,
                from: sender,
                parcel: parcel
            });
        }

        function getDefaults() {
            return dataService.getAll(`${settings.api.postage}/packagesize`);
        }

        function createDefaults(defaults) {
            return dataService.create(`${settings.api.postage}/packagesize`, defaults);
        }

        function purchaseLabel(rateId, orderId, labelType, printReturnLabel) {
            return dataService.create(`${settings.api.postage}/paid`, { rateId, orderId, labelType, printReturnLabel });
        }

        function getPurchasedLabel(orderId) {
            return dataService.get(`${settings.api.postage}/purchasedlabel`, orderId);
        }

        function refundLabel(shippoLabelId) {
            return dataService.save(`${settings.api.postage}/refund`, shippoLabelId);
        }

        function saveDefaults(postageDefaults, primaryStoreAddress) {
            let promises = [dataService.saveAll(`${settings.api.postage}/packagesize`, postageDefaults)];
            if (primaryStoreAddress) {
                promises.push(storeAddressesService.updatePrimaryAddress(primaryStoreAddress));
            }
            return $q.all(promises);
        }

        function openRefundShippingLabelModal(label, event) {
            return $translate([
                'BUTTON.CANCEL',
                'POSTAGEFULFILLMENT.REFUNDMODAL_TITLE',
                'POSTAGEFULFILLMENT.REFUNDMODAL_DESCRIPTION',
                'POSTAGEFULFILLMENT.REFUNDMODAL_OKBUTTON'
            ], { refundamount: convertCentsToDollars(label.invoice.amount) }).then((translations) => {
                return $mdDialog.show(
                    $mdDialog.confirm()
                        .title(translations['POSTAGEFULFILLMENT.REFUNDMODAL_TITLE'])
                        .htmlContent(`<p class="refund-label-modal-content">
                            ${translations['POSTAGEFULFILLMENT.REFUNDMODAL_DESCRIPTION']}
                        </p>`)
                        .targetEvent(event)
                        .ok(translations['POSTAGEFULFILLMENT.REFUNDMODAL_OKBUTTON'])
                        .cancel(translations['BUTTON.CANCEL']));
            });
        }

        function openRedirectToSetupModal(error) {
            return $translate([
                'BUTTON.CANCEL',

                'POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NOCARD',
                'POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NOADDRESS',
                'POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NODEFAULTS',

                'POSTAGEDEFAULTSSETUP.REDIRECTMODAL_DESCRIPTION',
                'POSTAGEDEFAULTSSETUP.REDIRECTMODAL_OKBUTTON'
            ]).then((translations) => {
                let modalTitle;
                if (error.errorType === 'noStoreAddress') {
                    modalTitle = translations['POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NOADDRESS'];
                } else if (error.errorType === 'noPaymentMethod') {
                    modalTitle = translations['POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NOCARD'];
                } else if (error.errorType === 'noDefaults') {
                    modalTitle = translations['POSTAGEDEFAULTSSETUP.REDIRECTMODAL_TITLE_NODEFAULTS'];
                }

                return $mdDialog.show(
                    $mdDialog.confirm()
                        .title(modalTitle)
                        .textContent(translations['POSTAGEDEFAULTSSETUP.REDIRECTMODAL_DESCRIPTION'])
                        .ok(translations['POSTAGEDEFAULTSSETUP.REDIRECTMODAL_OKBUTTON'])
                        .cancel(translations['BUTTON.CANCEL'])
                ).then(() => {
                    $state.go('postage.setup');
                });
            });
        }

        function generateRecipient(order) {
            return {
                name: `${order.deliverTo.firstName} ${order.deliverTo.lastName}`,
                street: order.deliverTo.address.address1,
                street2: order.deliverTo.address.address2,
                city: order.deliverTo.address.city,
                state: order.deliverTo.address.state,
                zip: order.deliverTo.address.postalCode,
                phone: order.deliverTo.phoneNumber,
                email: order.placedBy.emailAddress
            };
        }

        function generateSender(address, storeAdmin) {
            return {
                name: `${storeAdmin.firstName} ${storeAdmin.lastName}`,
                street: address.mailingAddress.address1,
                street2: address.mailingAddress.address2,
                city: address.mailingAddress.city,
                state: address.mailingAddress.state,
                zip: address.mailingAddress.postalCode,
                phone: address.phoneNumber,
                email: storeAdmin.email
            };
        }

        function hasConfiguredDefaults(defaults) {
            return angular.isDefined(defaults.labelType);
        }

        function showErrorToast(message) {
            $mdToast.show($mdToast.simple().textContent(message).position('bottom left'));
        }

        function convertCentsToDollars(cents) {
            return $filter('currency')((cents / 100).toFixed(2));
        }

        function getWeightOfCartItems(cartItems) {
            return reduce(cartItems, (total, item) => {
                return item.product.weight ? total + (item.product.weight * item.quantity) : total;
            }, 0);
        }

        function getQuantityOfCartItems(cartItems) {
            return reduce(cartItems, (total, item) => {
                return total + item.quantity;
            }, 0);
        }

        function getCarrierFromToken(token) {
            if (!isString(token)) return;
            return token === 'custom' ? token : token.split('_', 1)[0].toLowerCase();
        }

        function getNumOfItemsMissingWeight(cartItems) {
            return reduce(cartItems, (total, item) => {
                return !item.product.weight || item.product.weight === 0 ? total + 1 : total;
            }, 0);
        }

        return service;
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/postage.service.js